import axios from "axios";
import useUserStore from "../Store/UserStore.js";
import type {
	UserDataCallbacks,
	UserDataResponse,
	GetUserDataFunction,
	CharactersCallback,
	NotificationProps,
	LeaderboardUser,
	LeaderboardResponse,
	LeaderPosition,
	LeaderPositionResponse,
	Referrer,
	ReferrerUser,
	ShareStories,
	ReferrerResponse,
	ReferrerUSDT,
	ReferrerUSDTUser,
	ReferrerUSDTResponse,
	TaskStatus,
	ClaimTaskResponse,
	BoostMiningResponse,
	PurchaseResponse,
	FreezedTasksRequest,
	FreezedTasksResponse,
	VoucherResponse,
	VoucherErrorResponse,
	ApiResponse,
	ClaimTaskGroupResponse,
	OwnedCharacter,
	SetMainResponse,
	CharactersResponse,
	RetryableFunction,
	ContentLinkResponse,
	UserActiveResponse,
	BarzaTaskExchangeResponse,
	RaffleShortInfoResponse,
} from "../../types/api.ts";
import { useNotificationStore } from "../Store/NotificationStore";
import { Task } from "../Pages/Tasks_new/types.js";

export const settings = {
	url:
		import.meta.env.MODE === "development"
			? import.meta.env.VITE_API_URL_DEV
			: import.meta.env.MODE === "production"
			? import.meta.env.VITE_API_URL_PROD
			: import.meta.env.VITE_API_URL_DEV,
};

// Services
const tg = window.Telegram.WebApp;
import { useMiningStore } from "../Store/miningStore";
import {
	Expedition,
	ExpeditionClaimRewardResponse,
} from "../Pages/Expeditions/types.js";

if (import.meta.env.MODE === "development") {
	axios.defaults.params = { test_user_id: tg.initDataUnsafe.user.id };
	// axios.defaults.params = { test_user_id: 576590544 };
} else {
	axios.defaults.params = { initData: tg.initData };
}

export function getBalance(
	userId: number,
	callback: (balance: number) => void = () => {}
): Promise<number | undefined> {
	return axios
		.get<{ status: number; balance: number }>(
			`${settings.url}api/balance/${userId}`
		)
		.then(response => {
			if (response.data.status === 200) {
				const { setBalance } = useUserStore.getState();
				const newBalance = response.data.balance;

				setBalance(newBalance);
				callback(newBalance);

				return newBalance;
			}
		})
		.catch(err => {
			throw err;
		});
}

// Claim Bonus
export function claimBonus(callback = () => {}) {
	axios
		.post(`${settings.url}api/bonus/activated_prestart/`, {
			validation_data: tg.initData,
		})
		.then(response => {
			if (response.data.status == 200) {
				callback();
				getBalance(tg.initDataUnsafe.user.id);
			}
		})
		.catch(err => {
			console.log(err);
		});
}

export const getUserData: GetUserDataFunction = function (
	callbacks: UserDataCallbacks = {}
): Promise<UserDataResponse | null> {
	const store = useUserStore.getState();

	if (
		getUserData.isLoading ||
		store.questDataLoaded ||
		(getUserData.lastCallTimestamp &&
			Date.now() - getUserData.lastCallTimestamp < 1000)
	) {
		return Promise.resolve(null);
	}

	getUserData.isLoading = true;
	getUserData.lastCallTimestamp = Date.now();

	return axios
		.post<UserDataResponse>(`${settings.url}api/user_main/`, {
			validation_data: tg.initData,
		})
		.then(response => {
			const data = response.data;

			if (
				data.info.user_info["pre-start"] &&
				tg.initDataUnsafe.user.id != 5455842028
			) {
				callbacks.onPrestart?.();
			}
			if (!data.info.user_info?.tasks_status) {
				callbacks.onTasksComplete?.();
			}
			callbacks.onQuestData?.(data.date_quest_end, data.date_quest_end);

			return data;
		})
		.catch(err => {
			console.error("Error in getUserData:", err);
			throw err;
		})
		.finally(() => {
			getUserData.isLoading = false;
		});
};

getUserData.isLoading = false;
getUserData.lastCallTimestamp = null;

// Get Characters
export function getCharacters(callback: CharactersCallback = () => {}) {
	axios
		.get<{ status: number } & CharactersResponse>(
			`${settings.url}api/characters`
		)
		.then(response => {
			if (response.data.status === 403) {
				callback({ owned_characters: [], available_characters: [] });
			} else if (
				response.data.owned_characters &&
				response.data.available_characters
			) {
				callback({
					owned_characters: response.data.owned_characters,
					available_characters: response.data.available_characters,
				});
			} else {
				callback({ owned_characters: [], available_characters: [] });
			}
		})
		.catch(() => {
			callback({ owned_characters: [], available_characters: [] });
		});
}

// Get Tasks

export function getTasks(
	callback = (tasks: any[]) => {
		console.log("have not a callback");
	}
) {
	axios
		.get(`${settings.url}api/get_tasks/${tg.initDataUnsafe.user.id}`)
		.then(response => {
			console.log(response);
			if (response.data.status == 200) {
				callback(response.data.data.tasks);
				return response.data.data.tasks;
			}
		})
		.catch(err => {
			console.log(err);
		});
}

// Get Leaders USDT

export function getLeadersUSDT(
	callback: (data: {
		month: LeaderboardUser[];
		alltime: LeaderboardUser[];
	}) => void = () => {},
	userInTop: (data: {
		month: number;
		all_time: number;
		total_farm_month: number;
		total_farm_all: number;
	}) => void = () => {}
) {
	axios
		.get<LeaderboardResponse>(
			`${settings.url}api/leaders_cash/${tg.initDataUnsafe.user.id}`
		)
		.then(response => {
			if (response.data.status === 200) {
				callback({
					month: response.data.data.info_month,
					alltime: response.data.data.info_all_time,
				});
				userInTop({
					month: response.data.data.place_info_month.rank,
					all_time: response.data.data.place_info_all.rank,
					total_farm_month: response.data.data.place_info_month.total_farm,
					total_farm_all: response.data.data.place_info_all.total_farm,
				});
			}
		})
		.catch(err => {
			console.log(err);
		});
}

// Get Leaders DC

export function getLeaders(
	callback: (data: {
		month: LeaderboardUser[];
		alltime: LeaderboardUser[];
	}) => void = () => {}
) {
	axios
		.get<LeaderboardResponse>(
			`${settings.url}api/leaders_mine/${tg.initDataUnsafe.user.id}`
		)
		.then(response => {
			if (response.data.status === 200) {
				callback({
					month: response.data.data.info_month,
					alltime: response.data.data.info_all_time,
				});
			}
		})
		.catch(err => {
			console.log(err);
		});
}

// Get Position In Leader list DC
export function getPositionInLeaderList(
	callback: (data: LeaderPosition) => void = () => {}
) {
	axios
		.get<LeaderPositionResponse>(
			`${settings.url}api/position_leaders_mine/${tg.initDataUnsafe.user.id}`
		)
		.then(response => {
			if (response.data.status === 200) {
				callback(response.data.data);
			}
		})
		.catch(err => {
			console.log(err);
		});
}

// Get Referrers
export function getReferrers(
	callback: (
		elements: Referrer[],
		user: ReferrerUser,
		storiesInfo: ShareStories
	) => void = () => {}
): Promise<void> {
	return axios
		.get<ReferrerResponse>(
			`${settings.url}api/ref_system/${
				tg.initDataUnsafe.user.id == 5455842028
					? 576590544
					: tg.initDataUnsafe.user.id
			}`
		)
		.then(response => {
			if (response.data.status === 200) {
				const formattedData = {
					...response.data.data,
					amount_all: Number(response.data.data.amount_all).toFixed(4),
				};
				callback(
					response.data.data.mine_ref,
					{
						amount_all: formattedData.amount_all,
						link: response.data.data.link,
					},
					response.data.data.share_stories
				);
			}
		})
		.catch(err => {
			console.error("Ошибка при получении рефералов:", err);
		});
}

export function getReferrersUSDT(
	callback: (
		elements: ReferrerUSDT[],
		user: ReferrerUSDTUser
	) => void = () => {}
): Promise<void> {
	return axios
		.get<ReferrerUSDTResponse>(
			`${settings.url}api/usdt_ref/${
				tg.initDataUnsafe.user.id == 5455842028
					? 576590544
					: tg.initDataUnsafe.user.id
			}`
		)
		.then(response => {
			if (response.data.status === 200) {
				const formattedData = {
					...response.data.data,
					amount_all: Number(response.data.data.amount_all).toFixed(4),
				};
				callback(response.data.data.usdt_ref, {
					amount_all: formattedData.amount_all,
					link: response.data.data.link,
				});
			}
		})
		.catch(err => {
			console.error("Ошибка при получении USDT рефералов:", err);
		});
}

// Claim task

export function claimTask(
	id: number,
	callback: (status: TaskStatus, text?: string) => void = () => {
		console.log("have not callback ");
	}
): Promise<void> {
	return axios
		.post<ClaimTaskResponse>(`${settings.url}api/claim_task/`, {
			validation_data: tg.initData,
			task_id: id,
		})
		.then(response => {
			if (response.data.status === 200) {
				callback("done", "Выполнено");
			}
		})
		.catch(err => {
			console.error("Ошибка при получении награды за задание:", err);
			callback("start", "Подписаться");
		});
}

export function refClaim(callback = () => {}) {
	axios
		.post(`${settings.url}api/ref_claim/`, null)
		.then(response => {
			if (response.data.status == 200) {
				getUserBalance()
					.then(() => {
						callback();
					})
					.catch(() => {
						callback();
					});
			}
		})
		.catch(() => {
			// Разблокируем кнопку при ошибке
			callback();
		});
}

export function refClaimUSDT(
	callback = () => {},
	setNotification: (notification: NotificationProps) => void
) {
	axios
		.post(`${settings.url}api/claim_usdt_ref/`, null)
		.then(response => {
			if (response.data.status == 200) {
				getUserBalance()
					.then(() => {
						callback();
					})
					.catch(() => {
						callback();
					});
			} else if (response.data.status == 403) {
				setNotification({
					type: "Error",
					message: response.data.detail,
				});
			}
		})
		.catch(() => {});
}

export function sendRefLinkInBot() {
	axios
		.post(`${settings.url}api/ref_link_send/`, {
			validation_data: tg.initData,
		})
		.then(() => {})
		.catch(err => {
			console.log(err);
		});
}

// send link in bot

export function sendLinkInBot(link: string) {
	axios
		.post(`${settings.url}api/send_link/`, {
			validation_data: tg.initData,
			link: link,
		})
		.then(() => {})
		.catch(() => {
			// console.log(err)
		});
}

export async function getMiningStatus_2() {
	try {
		const response = await axios.get(`${settings.url}api/mining`);
		console.log("====== Ответ от сервера (ДАННЫЕ МАЙНИНГА):", response.data);
		return response.data;
	} catch (err) {
		console.error("Ошибка при получении данных майнинга:", err);
		throw err;
	}
}

export async function claimMining_2(miningId: number | string) {
	try {
		const response = await axios.delete(
			`${settings.url}api/mining/${miningId}`
		);
		console.log(
			"====== Ответ от сервера (ПОЛУЧЕНИЕ НАГРАДЫ ЗА МАЙНИНГ):",
			response.data
		);
		return response.data;
	} catch (err) {
		console.error("Ошибка при получении данных майнинга:", err);
		throw err;
	}
}

export async function startMining_2() {
	try {
		const response = await axios.post(`${settings.url}api/mining`);
		return response.data;
	} catch (err) {
		console.error("Ошибка при запуске майнинга:", err);
		throw err;
	}
}

// Ускорить майнинг
export function boostMining(
	miningId: number | string,
	provider = "",
	setNotification: (notification: NotificationProps) => void,
	adType?: string
) {
	const config = {
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
		},
	};

	const data = {
		provider: provider.toLowerCase(),
		action: adType,
	};

	return axios
		.post<BoostMiningResponse>(
			`${settings.url}api/mining/${miningId}/boost`,
			data,
			config
		)
		.then(response => {
			if (!response.data) {
				throw new Error("No data in boost response");
			}

			if (adType === "claim") {
				return;
			} else {
				const { setMiningData } = useMiningStore.getState();
				console.log("response.data", response.data);

				// Формируем обновленные данные майнинга
				const updatedMiningData = {
					mining: response.data.mining,
					last_boost_time: response.data.last_boost_time,
					original_date_end: response.data.original_date_end,
					adsgram_ads_allowed: response.data.adsgram_ads_allowed,
				};

				// Обновляем данные в сторе
				setMiningData(updatedMiningData);

				if (setNotification) {
					setNotification({
						type: "Success",
						message: "Майнинг ускорен на 30 минут! 🚀",
					});
				}

				// Просто возвращаем данные ответа
				return response.data;
			}
		})
		.catch(error => {
			console.error("Error in boostMining:", error);
			throw error;
		});
}

export function purchaseCharacter(
	characterId: number,
	callback: (response: ApiResponse) => void = () => {}
): Promise<boolean> {
	const setNotification = useNotificationStore.getState().setNotification;

	const config = {
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
		},
	};

	return axios
		.post<PurchaseResponse>(
			`${settings.url}api/characters/${characterId}`,
			null,
			config
		)
		.then((response): boolean => {
			if (response.status === 200 && response.data) {
				setNotification({
					type: "Success",
					message: "Персонаж успешно куплен",
				});
				callback({ status: response.status, data: response.data });
				return true;
			}
			setNotification({ type: "Error", message: "Ошибка покупки" });
			callback({ status: response.status, data: null });
			return false;
		})
		.catch(err => {
			setNotification({ type: "Error", message: "Ошибка покупки" });
			callback({ status: err.response?.status || 500, data: null });
			return false;
		});
}

export function setCharacterToMain(
	characterId: number,
	callback: (characters: OwnedCharacter[] | null) => void = () => {}
): Promise<boolean> {
	const setNotification = useNotificationStore.getState().setNotification;

	const config = {
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
		},
	};

	return axios
		.patch<SetMainResponse>(
			`${settings.url}api/characters/${characterId}`,
			null,
			config
		)
		.then(response => {
			if (response.status === 200 && response.data) {
				// Показываем уведомление об успехе
				setNotification({
					type: "Success",
					message: "Персонаж установлен на главный экран",
				});

				if (response.data.owned_characters) {
					callback(response.data.owned_characters);
					return true;
				} else {
					callback(null);
					return true;
				}
			}
			setNotification({
				type: "Error",
				message: "Ошибка при установке персонажа на главный экран",
			});
			callback(null);
			return false;
		})
		.catch(err => {
			console.error("setCharacterToMain error:", err);
			setNotification({
				type: "Error",
				message: "Ошибка сети при установке персонажа на главный экран",
			});
			callback(null);
			return false;
		});
}

export function getUserBalance(animateBalance = false) {
	return axios
		.get(`${settings.url}api/users/balance/`)
		.then(response => {
			console.log("Ответ на запрос баланса:", response);
			if (response.data) {
				const { setBalanceUSDT, setBalanceDC, setTickets } =
					useUserStore.getState();
				const newBalanceUSDT = response.data.usdt;
				const newBalanceDC = response.data.dc;
				const newTickets = response.data.tickets;

				setTickets(newTickets);

				// Обновляем стор с флагом анимации
				useUserStore.setState({
					balanceAnimationEnabled: animateBalance,
				});

				setBalanceUSDT(newBalanceUSDT);
				setBalanceDC(newBalanceDC);
			}
		})
		.catch(err => {
			throw err;
		});
}

export function sendWithdrawalRequest() {
	return axios
		.post(`${settings.url}api/users/balance/withdraw`, null)
		.then(() => {
			return true;
		})
		.catch(err => {
			console.error("Ошибка при отправке запроса на вывод:", err);
			return false;
		});
}

const retryRequest = <T>(
	fn: RetryableFunction<T>,
	retries: number = 3,
	delay: number = 2000
): Promise<T> => {
	return new Promise<T>((resolve, reject) => {
		fn()
			.then(resolve)
			.catch((error: Error) => {
				if (retries === 0) {
					reject(error);
					return;
				}
				setTimeout(() => {
					retryRequest(fn, retries - 1, delay)
						.then(resolve)
						.catch(reject);
				}, delay);
			});
	});
};

// Update Users Onboarding Status
export function updateUserOnboardingStatus() {
	return axios
		.patch(`${settings.url}api/users/onboarding`, null)
		.then(response => {
			return response.data;
		})
		.catch(err => {
			console.error("Error updating onboarding status:", err);
			throw err;
		});
}

//  Start Spin
export function getSpinInfo() {
	return axios
		.post(`${settings.url}api/spin`, null)
		.then(response => {
			if (response.data) {
				return response.data;
			}
			throw new Error("Пустой ответ от сервера");
		})
		.catch(err => {
			console.error("Детали ошибки:", {
				status: err.response?.status,
				statusText: err.response?.statusText,
				data: err.response?.data,
			});
			throw err;
		});
}

export function getFreezedTasks(
	callback: (data: FreezedTasksResponse) => void = () => {}
): Promise<FreezedTasksResponse> {
	const tg = window.Telegram.WebApp;
	const userId = tg.initDataUnsafe.user.id;

	const requestData: FreezedTasksRequest = {
		tg_user_id: userId,
		tg_user_locale: "ru",
		tg_user_first_name: tg.initDataUnsafe.user.first_name,
		keep_limit: 999,
		tg_user_is_premium: tg.initDataUnsafe.user.is_premium || false,
		tg_user_platform: tg.platform,
	};

	console.log(
		"Отправляем запрос на получение замороженных задач:",
		requestData
	);

	return axios
		.post<FreezedTasksResponse>(
			`${settings.url}api/barza/original_tasks`,
			requestData
		)
		.then(response => {
			console.log("====Ответ от сервера (BARZHA`):", response.data);
			callback(response.data);
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при получении замороженных задач:", err);
			throw err;
		});
}

// Get Task Tg Link (BARZHA)
export function getTaskTgLink(taskId: number | string) {
	const tg = window.Telegram.WebApp;
	const userId = tg.initDataUnsafe.user.id;

	const requestData = {
		tg_user_id: userId,
		tg_user_locale: "ru", // tg.initDataUnsafe.user.language_code
		tg_user_first_name: tg.initDataUnsafe.user.first_name,
		tg_user_is_premium: tg.initDataUnsafe.user.is_premium || false,
		tg_user_platform: tg.platform,
	};

	return axios
		.post(`${settings.url}api/barza/original_tasks/${taskId}/link`, requestData)
		.then(response => {
			console.log("Ответ от сервера (клик по заданию):", response.data);
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при клике по заданию:", err);
			throw err;
		});
}

// Get Check Status (BARZHA)
export function getCheckStatus() {
	return axios.get(`${settings.url}api/barza/tasks`).then(response => {
		console.log(
			"===Ответ от сервера (проверка статуса отображения BARZHA):",
			response.data
		);
		return response.data;
	});
}

// Get Check Status (TAPPADS)
export function getCheckStatusTappAds() {
	return axios
		.get(`${settings.url}api/tappads/tasks`)
		.then(response => {
			console.log(
				"===ответ от сервера (проверка статуса отображения (tappads)):",
				response.data
			);
			return response.data;
		})
		.catch(err => {
			console.error("ошибка при проверке статуса отображения:", err);
			throw err;
		});
}

// Get Check Subscription Status (TAPPADS)
export function getCheckSubscriptionStatusTappAds(taskId: number) {
	return axios
		.post(`${settings.url}api/tappads/tasks/${taskId}/claim`, null)
		.then(response => {
			console.log(
				"===Ответ от сервера (проверка статуса подписки (TAPPADS)):",
				response.data
			);
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при проверке статуса подписки:", err);
			throw err;
		});
}

// Get Check Subscription Status from BARZHA
export function getCheckSubscriptionStatus(taskId: number) {
	return axios
		.post(`${settings.url}api/barza/tasks/${taskId}/claim`, null)
		.then(response => {
			console.log(
				"===Ответ от сервера (проверка статуса подписки (BARZHA)):",
				response.data
			);
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при проверке статуса подписки:", err);
			throw err;
		});
}

// Get check status from Flyer
export async function getCheckStatusFlyer() {
	return axios
		.get(`${settings.url}api/tasks/flyer/get_tasks`)
		.then(response => {
			console.log(
				"ответ от сервера (проверка статуса отображения (flyer)):",
				response.data
			);
			return response.data;
		})
		.catch(err => {
			console.error("ошибка при проверке статуса отображения:", err);
			throw err;
		});
}

// Get check sub status from Flyer
export async function getCheckSubStatusFlyer(
	task_id: string,
	title: string,
	description: string,
	image_url: string
) {
	const body = {
		task_id,
		title,
		description,
		image_url,
	};

	return axios
		.post(`${settings.url}api/tasks/flyer/check_task/${task_id}`, body)
		.then(response => {
			console.log(
				"oтвет от сервера (проверка статуса подписки (flyer)):",
				response.data
			);
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при проверке статуса подписки:", err);
			throw err;
		});
}

// Get tasks from Traffy
export async function getTraffyTasks() {
	return axios
		.get(`${settings.url}api/traffy/tasks`)
		.then(response => {
			console.log(
				"ответ от сервера (получение заданий (traffy)):",
				response.data
			);
			return response.data;
		})
		.catch(err => {
			console.error("ошибка при получении заданий Traffy:", err);
			throw err;
		});
}

// Claim task from Flyer
export async function claimFlyerTask(taskId: string) {
	try {
		const response = await axios.post(
			`${settings.url}api/tasks/flyer/claim_task/${taskId}`
		);
		console.log("Ответ от сервера (захват задания):", response.data);
		return response.data;
	} catch (error) {
		console.error("Ошибка при захвате задания:", error);
		throw error;
	}
}

// Claim task from Flyer
export async function claimTraffyTask(signedToken: string) {
	try {
		const response = await axios.post(
			`${settings.url}api/traffy/claim_task/${signedToken}`
		);
		console.log("Ответ от сервера (захват задания):", response.data);
		return response.data;
	} catch (error) {
		console.error("Ошибка при захвате задания:", error);
		throw error;
	}
}

// Get partner tasks
export function getPartnerTasks() {
	return axios
		.get(`${settings.url}api/tasks/partner-click`)
		.then(response => {
			console.log(
				"====== Ответ от сервера (ПАРТНЕРСКИЕ ЗАДАНИЯ (DREAMCOIN)):",
				response.data
			);
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при получении партнерских заданий:", err);
			throw err;
		});
}

// Post partner tasks
export function postPartnerTasks(taskId: string | number) {
	return axios
		.post(`${settings.url}api/tasks/partner-click/${taskId}`, null)
		.then(response => {
			console.log(
				"====== КЛИК ПО ЗАДАНИЮ (ПАРТНЕРСКИЕ ЗАДАНИЯ (DREAMCOIN)):",
				response.data
			);
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при клике по партнерскому заданию:", err);
			throw err;
		});
}

// Post partner tasks get reward
//получение награды за  задания
export function postPartnerTasksGetReward(taskId: number) {
	return axios
		.post(`${settings.url}api/tasks/${taskId}/claim`, null)
		.then(response => {
			console.log(
				"====== ПОЛУЧЕНИЕ НАГРАДЫ (ПАРТНЕРСКИЕ ЗАДАНИЯ (PARTNER TASKS)):",
				response.data
			);
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при получении награды:", err);
			throw err;
		});
}

// Get Partner Subscription Tasks
export function getPartnerSubscriptionTasks() {
	return axios
		.get(`${settings.url}api/tasks/partner-subscription`)
		.then(response => {
			console.log(
				"====== Ответ от сервера (PARTNER SUBSCRIPTION TASKS):",
				response.data
			);
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при получении заданий подписки:", err);
			throw err;
		});
}

// Activate Voucher
export function activateVoucher(
	voucherCode: string,
	callback: (data: VoucherResponse) => void = () => {},
	errorCallback: (error: {
		response: { data: VoucherErrorResponse };
	}) => void = () => {}
): void {
	axios
		.post<VoucherResponse>(
			`${settings.url}api/voucher`,
			{
				voucher_code: voucherCode,
			},
			{
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
			}
		)
		.then(response => {
			if (response.status === 200) {
				const { type, amount, new_balance } = response.data;
				callback({ type, amount, new_balance });
			}
		})
		.catch(err => {
			console.error("Ошибка при активации ваучера:", err);
			console.log(err.response.data.detail);
			errorCallback(err);
		});
}

// Get Ad Widget from BARZHA
export function getAdWidget() {
	const tg = window.Telegram?.WebApp;

	// Если WebApp не инициализирован, возвращаем Promise.reject
	if (!tg?.initDataUnsafe?.user) {
		return Promise.reject(new Error("Telegram WebApp not initialized"));
	}

	const userId = tg.initDataUnsafe.user.id;

	const requestData = {
		tg_user_id: userId,
		tg_user_locale: "ru",
		tg_user_first_name: tg.initDataUnsafe.user.first_name,
		tg_user_is_premium: tg.initDataUnsafe.user.is_premium || false,
		tg_user_platform: tg.platform,
	};

	return axios
		.post(`${settings.url}api/barza/original_widget`, requestData)
		.then(response => {
			console.log("Ответ от сервера (виджет рекламы):", response.data);
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при получении виджета рекламы:", err);
			throw err;
		});
}

// Get Ad Widget Link
export const getAdWidgetLink = async (adCampaignId: number) => {
	const tg = window.Telegram.WebApp;
	const userId = tg.initDataUnsafe.user.id;
	try {
		const requestData = {
			tg_user_id: userId,
			tg_user_locale: "ru",
			tg_user_first_name: tg.initDataUnsafe.user.first_name,
			tg_user_is_premium: tg.initDataUnsafe.user.is_premium || false,
			tg_user_platform: tg.platform,
		};

		const response = await axios.post(
			`${settings.url}api/barza/original_widget/${adCampaignId}/link`,
			requestData
		);

		return response.data;
	} catch (error) {
		console.error("Ошибка при получении ссылки на рекламу:", error);
		throw error;
	}
};

// Login
export function loginUser(userAgent: string) {
	const tg = window.Telegram.WebApp;
	// @ts-ignore - allows_write_to_pm exists in Telegram WebApp API but not in type definitions
	const isBlocked = tg.initDataUnsafe.user.allows_write_to_pm;
	// @ts-ignore - start_param exists in Telegram WebApp API but not in type definitions
	const startParam = tg.initDataUnsafe.start_param || null;
	const encodedInitData = encodeURIComponent(tg.initData);

	const requestBody = JSON.stringify({
		user_agent: userAgent,
	});

	if (!isBlocked) {
		return new Promise((resolve, reject) => {
			// @ts-ignore - requestWriteAccess exists in Telegram WebApp API but not in type definitions
			tg.requestWriteAccess(async (result: boolean) => {
				if (result) {
					try {
						const response = await axios.post(
							`${settings.url}api/users/login/${startParam}?initData=${encodedInitData}`,
							requestBody,
							{
								headers: {
									"Content-Type": "application/json",
								},
							}
						);
						resolve({ data: response.data, startup_code: startParam });
					} catch (error) {
						console.error("[loginUser] Error:", error);
						reject(error);
					}
				} else {
					// @ts-ignore - close exists in Telegram WebApp API but not in type definitions
					tg.close();
					reject(new Error("Write access denied"));
				}
			});
		});
	} else {
		return axios
			.post(
				`${settings.url}api/users/login/${startParam}?initData=${encodedInitData}`,
				requestBody,
				{
					headers: {
						"Content-Type": "application/json",
					},
				}
			)
			.then(response => {
				return { data: response.data, startup_code: startParam };
			})
			.catch(error => {
				console.error("[loginUser] Error:", error);
				throw error;
			});
	}
}

// Get Tasks Group
export function getTasksGroup(callback: (data: any) => void) {
	axios
		.get(`${settings.url}api/tasks-groups`)
		.then(response => {
			console.log("Ответ от сервера (группа заданий):", response.data);
			callback(response.data);
		})
		.catch(err => {
			console.error("Ошибка при получении группы заданий:", err);
			throw err;
		});
}

// Get Tasks Group Details
export function getTasksGroupDetails(
	id: number,
	callback: (data: any) => void
) {
	axios
		.get(`${settings.url}api/tasks-groups/${id}`)
		.then(response => {
			console.log("Ответ от сервера (детали группы заданий):", response.data);
			callback(response.data);
		})
		.catch(err => {
			console.error("Ошибка при получении деталей группы заданий:", err);
			throw err;
		});
}

export function claimTaskGroup(
	id: number,
	callback: (response: ClaimTaskGroupResponse) => void,
	errorCallback?: (error: {
		message: string;
		code?: number;
		details?: any;
	}) => void
): void {
	axios
		.post<ClaimTaskGroupResponse>(`${settings.url}api/tasks-groups/${id}/claim`)
		.then(response => {
			console.log("Ответ от сервера (захват группы заданий):", response.data);
			callback(response.data);
		})
		.catch(err => {
			console.error("Ошибка при захвате группы заданий:", err);
			if (errorCallback) {
				errorCallback(err);
			}
		});
}

export function getDailyRewards() {
	return axios
		.get(`${settings.url}api/tasks-groups/7`)
		.then(response => {
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при получении ежедневных наград:", err);
			throw err;
		});
}

export async function sendContentLink(
	link: string
): Promise<ContentLinkResponse> {
	const requestData = {
		link: link,
	};
	try {
		const response = await axios.post<ContentLinkResponse>(
			`${settings.url}api/promo`,
			requestData
		);
		console.log("Ссылка отправлена:", response.data);
		return response.data;
	} catch (error) {
		console.error("Ошибка при отправке запроса:", error);
		throw error;
	}
}

// Send Users Active
export async function sendUsersActive(): Promise<UserActiveResponse> {
	try {
		const response = await axios.post<UserActiveResponse>(
			`${settings.url}api/users/action`
		);
		console.log(
			"====== Ответ от сервера (ПРОВЕРКА АКТИВНОСТИ ПОЛЬЗОВАТЕЛЕЙ):",
			response.data
		);
		return response.data;
	} catch (error) {
		console.error("Ошибка при проверке активности пользователей:", error);
		throw error;
	}
}

// Claim Barza Task Exchange
export async function claimBarzaTaskExchange(
	id: number
): Promise<BarzaTaskExchangeResponse> {
	try {
		const response = await axios.post<BarzaTaskExchangeResponse>(
			`${settings.url}api/barza/task-exchange/${id}/claim`
		);
		console.log("Ответ от сервера (захват задания):", response.data);
		return response.data;
	} catch (error) {
		console.error("Ошибка при захвате задания:", error);
		throw error;
	}
}

export async function getFullGiveaway(raffle_id: number) {
	try {
		const response = await axios.get(`${settings.url}api/raffle/${raffle_id}`);
		return response.data;
	} catch (e) {
		console.log(e);
	}
}

export async function getShortGiveaway() {
	try {
		const response = await axios.get(`${settings.url}api/raffle`);
		return response.data;
	} catch (e) {
		console.log(e);
	}
}

// Get Raffle short info
export async function getRaffleShortInfo(): Promise<RaffleShortInfoResponse> {
	try {
		const response = await axios.get<RaffleShortInfoResponse>(
			`${settings.url}api/raffle`
		);
		return response.data;
	} catch (error) {
		console.error(
			"Ошибка при получении краткой информации о розыгрыше:",
			error
		);
		throw error;
	}
}

export async function payDC(data: {
	id: number;
	amount: number;
	coupon_type: string;
}) {
	try {
		const response = await axios.post(
			`${settings.url}api/raffle/coupons`,
			data
		);
		return response.data;
	} catch (e) {
		console.log(e);
	}
}

export async function claimGiveaway(id: number, place: number) {
	try {
		const response = await axios.post(
			`${settings.url}api/raffle/${id}/claim?place=${place}`
		);
		return response.data;
	} catch (e) {
		console.log(e);
	}
}

// GET Expedition INFO

export async function getExpeditionInfo(id: number): Promise<Expedition> {
	return axios
		.get(`${settings.url}expeditions/${id.toString()}`)
		.then(response => {
			console.log("информация об экспедиции получена", response.data);
			return response.data;
		})
		.catch(err => {
			console.error("ошибка при получении информации об экспедиции", err);
			throw err;
		});
}

// Claim Expedition Reward
export async function claimExpeditionReward(
	id: number = 1,
	params?: { id?: number; currency?: string }
): Promise<ExpeditionClaimRewardResponse> {
	const queryParam =
		params && params.id
			? `?prize_id=${params.id}`
			: params && params.currency
			? `?currency=${params.currency}`
			: "";

	return axios
		.post(`${settings.url}expeditions/claim/${id}${queryParam}`)
		.then(response => response.data)
		.catch(err => console.log("ошибка при получении награды экспедиции", err));
}

// Purchase Coupons DC
export async function purchaseCoupons(
	id: number,
	couponType: string,
	amount: number
) {
	try {
		const response = await axios.post(
			`${settings.url}api/raffle/${id}/coupons`,
			{
				coupon_type: couponType,
				amount: amount,
			}
		);
		console.log("Ответ от сервера (покупка купонов):", response.data);
		return response.data;
	} catch (error) {
		console.error("Ошибка при покупке купонов:", error);
		throw error;
	}
}

// Get Invoice STAR
export async function getInvoice(data: {
	title: string;
	description: string;
	amount: number;
	id: number;
	item_type: string;
	details?: Record<string, any>;
}) {
	console.log(data);
	try {
		const response = await axios.post(`${settings.url}api/payments/xtr`, data);
		return response.data;
	} catch (error) {
		console.error("Ошибка при получении счета:", error);
		throw error;
	}
}

// GET Banner CPC Dream

export async function getCPCBanner() {
	try {
		const response = await axios.get(`${settings.url}api/cpc/banner`);
		return response.data;
	} catch (error) {
		console.error("Ошибка при получении баннера CPC Dream:", error);
		throw error;
	}
}

// POST Banner CPC Dream Click
export async function postCPCBannerClick(banner_id: string) {
	try {
		const response = await axios.post(
			`${settings.url}api/cpc/click/action?banner_id=${banner_id}`
		);
		return response.data;
	} catch (error) {
		console.error("Ошибка при клике на баннер CPC Dream:", error);
		throw error;
	}
}

export async function checkSubscription(
	taskId: number,
	partner_id: number,
	isTrippled: boolean
) {
	try {
		if (isTrippled) {
			const response = await axios.post(
				`${settings.url}api/tasks/${taskId}/claim`,
				{
					details: {
						validation_type: "payment",
					},
				}
			);
			return response.data;
		} else {
			const response = await axios.post(
				`${settings.url}api/tasks/${taskId}/claim`,
				{
					details: {
						validation_type: "subscription",
						partner_id: partner_id,
					},
				}
			);
			return response.data;
		}
	} catch (error) {
		console.error("Ошибка при проверке подписки:", error);
		throw error;
	}
}

export async function addTaskToBlacklist(task?: Task | null) {
	return axios
		.post(`${settings.url}api/blacklist`, {
			task_name: [task!.title],
		})
		.then(response => {
			console.log(
				"Ответ от сервера (добавление задания в черный список):",
				response.data
			);
			return response.data;
		})
		.catch(err => {
			console.error("Ошибка при добавлении задания в черный список:", err);
			return { error: err.response };
		});
}
